import {
  createAsyncThunk,
  createSlice,
  isAction,
  isRejected,
} from "@reduxjs/toolkit";
import jobService from "../services/job.service";
import { logout } from "./loginSlice";
import {
  AssignApplicantReq,
  CommentReq,
  CreateInterviewStagesReq,
  CreateInterviewStagesRes,
  GetApplicantCommentsReq,
  GetInterviewStagesReq,
  Job,
  RejectApplicantReq,
  TagApplicantReq,
  unSaveJobReq,
  UpdateApplicantStageReq,
  UpdateInterviewStagesReq,
  withdrawToJobReq,
} from "../models/job.model";

interface JobReduxState {
  jobStages: Array<CreateInterviewStagesRes>;
  openedJobBeforelogin: { jobId: string };
  advanceJobSearch: any;
  jobSearchFields: {
    keyword: string;
    location: string;
  };
  jobCards: any;
  assignedJobsMemberNextUrl: string;
  isLoading: boolean;
  appliedJobs: any;
  savedJobs: any;
  jobApplicants: {
    applicants: any;
    isLoading: boolean;
    error: boolean;
  };
  error: boolean;
  jobById: {
    isLoading: boolean;
    error: boolean;
    jobDetail: any;
  };
  applicationLog: {
    logs: any;
    isLoading: boolean;
    error: boolean;
    sort: string;
  };
  isCandidateViewEnable: boolean;
  assignJobList: Array<Job>;
}

export interface GetAssignJobRequest {
  memberId: string;
  search?: string;
}

export interface GetApplicantsFilter {
  jobId: string;
  adviserId?: string;
  recruiterId?: string;
}

const initialState: JobReduxState = {
  openedJobBeforelogin: {
    jobId: "",
  },
  advanceJobSearch: null,
  jobSearchFields: {
    keyword: "",
    location: "",
  },
  jobStages: [
    {
      id: "",
      stageName: "Tagged List",
      jobId: "",
      createdBy: "",
      updatedBy: "",
      createdAt: "",
      updatedAt: "",
      edit: false,
    },
  ],
  jobCards: null,
  assignedJobsMemberNextUrl: "",
  isLoading: true,
  appliedJobs: null,
  savedJobs: null,
  jobApplicants: {
    applicants: null,
    isLoading: true,
    error: false,
  },
  applicationLog: {
    logs: [],
    isLoading: true,
    error: false,
    sort: "",
  },
  error: false,
  jobById: {
    isLoading: true,
    error: false,
    jobDetail: null,
  },
  isCandidateViewEnable: false,
  assignJobList: [],
};

export const createJobApi = createAsyncThunk(
  "job/createJobApi",
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.createJob(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateJobApi = createAsyncThunk(
  "job/updateJobApi",
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.updateJob(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getRecommendedJobApi = createAsyncThunk(
  "job/getRecommendedJobApi",
  async (_data, { getState, rejectWithValue, dispatch }) => {
    try {
      const response = await jobService.getRecommendedJobs(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getJobDetailApi = createAsyncThunk(
  "jobs/getJobDetailApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getJobDetail(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getJobByIdPopupApi = createAsyncThunk(
  "jobs/getJobByIdPopupApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getJobDetail(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const applyJobApi = createAsyncThunk(
  "jobs/applyToJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.applyToJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);
export const withdrawJobApi = createAsyncThunk(
  "jobs/withdrawJobApi",
  async (_data: withdrawToJobReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.withdrawToJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getAppliedJobApi = createAsyncThunk(
  "job/getAppliedJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getAppliedJob();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getApplicantDetailsByIdApi = createAsyncThunk(
  "job/getApplicantDetailsByIdApi",
  async (
    _data: { jobId: string; applicantId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const response = await jobService.getApplicantDetailsById(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const assignApplicantApi = createAsyncThunk(
  "job/assignApplicantApi",
  async (_data: AssignApplicantReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.assignApplicant(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getApplicantHistoryByIdApi = createAsyncThunk(
  "job/getApplicantHistoryByIdApi",
  async (
    _data: { applicantId: string; sort: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const response = await jobService.getApplicantHistoryById(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateJobStatusApi = createAsyncThunk(
  "job/updateJobStatusApi",
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.updateJobStatus(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateApplicantStatusApi = createAsyncThunk(
  "job/updateApplicantStatusApi",
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.updateApplication(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextJobApi = createAsyncThunk(
  "job/getNextFeedApis",
  async (a, { getState, rejectWithValue, dispatch }) => {
    try {
      const state: any = getState();
      const response = await jobService.getJobByUrl(
        state.jobs.jobCards.metadata.nextResultURL
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getSavedJobApi = createAsyncThunk(
  "jobs/getSavedJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getSavedJob();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextSavedJobApi = createAsyncThunk(
  "jobs/getNextSavedJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const response = await jobService.getJobByUrl(
        state.jobs.savedJobs.metadata.nextResultURL
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const saveJobApi = createAsyncThunk(
  "jobs/saveJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.saveJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unSaveJobApi = createAsyncThunk(
  "jobs/unSaveJobApi",
  async (_data: unSaveJobReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.unSaveJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getApplicantsApi = createAsyncThunk(
  "jobs/getApplicantsApi",
  async (_data: GetApplicantsFilter, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getApplicants(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getStagedApplicantsApi = createAsyncThunk(
  "jobs/getStagedApplicantsApi",
  async (_data: any, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getApplicants(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getStagedApplicantsNexturlApi = createAsyncThunk(
  "jobs/getStagedApplicantsNexturlApi",
  async (_data: string, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getNextStagedApplicants(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const tagApplicantApi = createAsyncThunk(
  "jobs/tagApplicantApi",
  async (_data: TagApplicantReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.tagApplicant(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const rejectApplicantApi = createAsyncThunk(
  "jobs/rejectApplicantApi",
  async (_data: RejectApplicantReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.rejectApplicant(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateApplicantStageApi = createAsyncThunk(
  "jobs/updateApplicantStageApi",
  async (_data: UpdateApplicantStageReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.updateApplicantStage(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextApplicantsApi = createAsyncThunk(
  "jobs/getNextApplicantsApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const response = await jobService.getJobByUrl(
        state?.jobs?.jobApplicants?.applicants?.metadata?.nextResultURL
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextApplicantsCMApi = createAsyncThunk(
  "jobs/getNextApplicantsCMApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await jobService.getJobApplicantsForCMByUrl(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const asignJobApi = createAsyncThunk(
  "jobs/asignJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.asignJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getAssignJobsToMemberApi = createAsyncThunk(
  "jobs/getAssignJobsToMemberApi",
  async (_data: GetAssignJobRequest, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getAssignJobsToMember(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextAssignJobsToMemberApi = createAsyncThunk(
  "jobs/getNextAssignJobsToMemberApi",
  async (_data: { memberId: string }, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const response = await jobService.getNextAssignedJobToMemberByUrl({
        ..._data,
        nextUrl: state.jobs.assignedJobsMemberNextUrl,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextJobsToMemberAdminApi = createAsyncThunk(
  "jobs/getNextJobsToMemberAdminApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const response = await jobService.getJobByUrl(
        state.jobs.jobCards?.metadata?.nextResultURL
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getNextAppliedJobsApi = createAsyncThunk(
  "jobs/getNextAppliedJobsApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const response = await jobService.getJobByUrl(
        state?.jobs?.appliedJobs?.metadata?.nextResultURL
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getAssignMembersToJobApi = createAsyncThunk(
  "jobs/getAssignMembersToJobApi",
  async (_data, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getAssignMembersToJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

//Interview Stages
export const getInterviewStagesApi = createAsyncThunk(
  "jobs/getInterviewStagesApi",
  async (_data: GetInterviewStagesReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getInterviewStages(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createInterviewStagesApi = createAsyncThunk(
  "jobs/createInterviewStagesApi",
  async (_data: CreateInterviewStagesReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.createInterviewStages(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateInterviewStagesApi = createAsyncThunk(
  "jobs/updateInterviewStagesApi",
  async (_data: UpdateInterviewStagesReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.updateInterviewStages(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const addCommentApi = createAsyncThunk(
  "jobs/addCommentApi",
  async (_data: CommentReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.addComment(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getApplicantCommentsApi = createAsyncThunk(
  "jobs/getApplicantCommentsApi",
  async (_data: GetApplicantCommentsReq, { getState, rejectWithValue }) => {
    try {
      const response = await jobService.getApplicantComments(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateCandidateReferJobApi = createAsyncThunk(
  "jobs/updateCandidateReferJobApi",
  async (
    _data: { applicationId: string; jobId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const response = await jobService.updateCandidateReferJob(_data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const jobSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    toggleApplicationToProfile: (state) => {
      state.isCandidateViewEnable = !state.isCandidateViewEnable;
    },
    jobOpenedbeforeLoginHandler: (state, action) => {
      state.openedJobBeforelogin.jobId = action.payload.jobId;
    },
    jobSearchFieldsReducer: (state, action) => {
      state.jobSearchFields.keyword = action.payload.keyword;
      state.jobSearchFields.location = action.payload.location;
    },
    advanceJobSearchReducer: (state, action) => {
      state.advanceJobSearch = action.payload;
    },
    addColumnReducer: (state, action) => {
      const isEditActive = state.jobStages.some((stage) => stage.edit === true);

      if (isEditActive) {
        throw new Error("");
        // Or handle the error differently, e.g., setting an error state.
      } else {
        state.jobStages.push({
          id: "",
          stageName: "",
          edit: true,

          jobId: "",
          createdBy: "",
          updatedBy: "",
          createdAt: "",
          updatedAt: "",
        });
      }
    },
    editCloumnhandler: (state, action) => {
      state.jobStages = action.payload.updatedJobStages;
    },
    exitColumnReducer: (state, action) => {
      state.jobStages = action.payload.updatedJobStages;
    },
    removeColumnReducer: (state) => {
      state.jobStages.pop();
    },
    sortLogsReducer: (state, action) => {
      state.applicationLog.sort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRecommendedJobApi.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(getRecommendedJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.jobCards = response;
      state.isLoading = false;
    });

    builder.addCase(getRecommendedJobApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = true;
    });

    builder.addCase(getAssignJobsToMemberApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.jobCards = response;
      state.assignJobList = response.jobs;
      state.assignedJobsMemberNextUrl = action.payload.data.nextPageUrl;
    });

    builder.addCase(getNextJobsToMemberAdminApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.jobs && response.jobs.length > 0) {
        state.jobCards.jobs = state.jobCards.jobs.concat(response.jobs);
      }
      state.jobCards.metadata = response.metadata;
    });

    builder.addCase(getNextAssignJobsToMemberApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.jobs && response?.jobs.length > 0) {
        state.jobCards.jobs = state.jobCards.jobs.concat(response.jobs);
      }
      state.assignedJobsMemberNextUrl = action.payload.data.nextPageUrl;
    });

    builder.addCase(getNextApplicantsApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.applications && response.applications.length > 0) {
        state.jobApplicants.applicants.applications =
          state?.jobApplicants?.applicants?.applications.concat(
            response.applications
          );
      }
      state.jobApplicants.applicants.metadata = response.metadata;
    });

    builder.addCase(getJobDetailApi.pending, (state, action) => {
      state.jobById.isLoading = true;
    });

    builder.addCase(getJobDetailApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.jobById.isLoading = false;
      state.jobById.jobDetail = response[0];
    });

    builder.addCase(getJobDetailApi.rejected, (state, action) => {
      state.jobById.isLoading = false;
      state.jobById.error = true;
    });

    builder.addCase(applyJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
    });

    builder.addCase(withdrawJobApi.fulfilled, (state, action) => {
      const jobId = action.meta.arg.jobId;
      const filterData = state.appliedJobs.jobs.filter(
        (appliedJob) => appliedJob?.job.id !== jobId
      );
      state.appliedJobs.jobs = filterData;
    });

    builder.addCase(getAppliedJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.appliedJobs = response;
    });

    builder.addCase(getNextAppliedJobsApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.jobs && response?.jobs.length > 0) {
        state.appliedJobs.jobs = state.appliedJobs.jobs.concat(response.jobs);
      }
      state.appliedJobs.metadata = response.metadata;
    });

    builder.addCase(createJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
    });

    builder.addCase(updateJobStatusApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
    });

    builder.addCase(getNextJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.jobs && response?.jobs?.length > 0) {
        state.jobCards.jobs = state.jobCards.jobs.concat(response.jobs);
      }
      state.jobCards.metadata = response.metadata;
    });

    builder.addCase(getSavedJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.savedJobs = response;
    });

    builder.addCase(getNextSavedJobApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      if (response?.jobs && response?.jobs?.length > 0) {
        state.savedJobs.jobs = state.savedJobs.jobs.concat(response.jobs);
      }
      state.savedJobs.metadata = response.metadata;
    });

    builder.addCase(unSaveJobApi.fulfilled, (state, action) => {
      const jobId = action.meta.arg.jobId;
      const response = action.payload.data.data;
      if (response) {
        state.savedJobs.jobs = state.savedJobs.jobs.filter(
          (job) => job.id !== jobId
        );
      }
      // state.savedJobs = response;
    });

    builder.addCase(getApplicantsApi.pending, (state, action) => {
      state.jobApplicants.isLoading = true;
      state.jobApplicants.error = false;
    });

    builder.addCase(getApplicantsApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.jobApplicants.applicants = response;
      state.jobApplicants.isLoading = false;
      state.jobApplicants.error = false;
    });

    builder.addCase(getApplicantsApi.rejected, (state, action) => {
      state.jobApplicants.isLoading = false;
      state.jobApplicants.error = true;
    });

    builder.addCase(updateCandidateReferJobApi.rejected, (state, action) => {
      console.log("hello World");
      let applicants = state.jobApplicants.applicants.applications.filter(
        (applicant) => applicant.id != action.meta.arg.applicationId
      );
      console.log(applicants, "@@@@@@ applicantsapplicants @@@@@@");
      state.jobApplicants.applicants.applications = applicants;
    });

    builder.addCase(rejectApplicantApi.fulfilled, (state, action) => {
      const applicantId = action.meta.arg.applicantId;
      const isRejected = action.meta.arg.isRejected;
      const rejectedBy = action.meta.arg.rejectedBy;
      const firstName = action.meta.arg.firstName;
      const lastName = action.meta.arg.lastName;

      const myArray = state.jobApplicants.applicants.applications.map(
        (applicant) => {
          if (applicantId === applicant.id) {
            return {
              ...applicant,
              isRejected: isRejected,
              rejectedBy: rejectedBy,
              assignedToDetails:
                applicant?.assignedToDetails &&
                applicant?.assignedToDetails.length > 0
                  ? applicant?.assignedToDetails?.map((details) => {
                      if (details?.memberId === rejectedBy) {
                        return {
                          ...details,
                          status: isRejected ? "Rejected" : "Pending",
                        };
                      }
                      return details;
                    })
                  : null,
              rejectedByDetails: {
                ...applicant?.rejectedByDetails,
                firstName: firstName,
                lastName: lastName,
              },
            };
          }
          return applicant;
        }
      );
      state.jobApplicants.applicants.applications = myArray;
    });

    builder.addCase(assignApplicantApi.fulfilled, (state, action) => {
      const response = action?.payload?.data?.success;

      if (response && response?.length > 0) {
        state.jobApplicants.applicants.applications.forEach((applicant) => {
          response.forEach((item) => {
            if (item?.applicationId === applicant?.id) {
              if (applicant.assignedToDetails) {
                applicant.assignedToDetails.push(item);
              } else {
                applicant.assignedToDetails = [item];
              }
            }
          });
        });
      }
    });

    // builder.addCase(updateApplicantStatusApi.fulfilled, (state, action) => {
    //   const response = action.payload.data.data;
    //   const updatedArray = state.jobApplicants.applicants.applications.map(
    //     (applicant) => {
    //       if (applicant.id === response.id) {
    //         return {
    //           ...applicant,
    //           status: response.status,
    //         };
    //       }
    //       return applicant;
    //     }
    //   );
    //   state.jobApplicants.applicants.applications = updatedArray;
    // });

    builder.addCase(logout, (state) => {
      // Reset this slice's state to initial state
      return initialState;
    });

    builder.addCase(getInterviewStagesApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      const mergedArray = [
        state.jobStages[0],
        ...response.map((item: CreateInterviewStagesRes) => ({
          ...item,
          edit: false,
        })),
      ];

      state.jobStages = mergedArray;
    });

    builder.addCase(createInterviewStagesApi.fulfilled, (state, action) => {
      const response = action.payload.data.data;
      state.jobStages[state.jobStages.length - 1] = {
        ...response,
        edit: false,
      };
    });

    builder.addCase(getApplicantHistoryByIdApi.pending, (state, action) => {
      state.applicationLog.isLoading = true;
      state.applicationLog.logs = [];
      state.applicationLog.error = false;
    });

    builder.addCase(getApplicantHistoryByIdApi.fulfilled, (state, action) => {
      const response = action?.payload?.data?.data;
      state.applicationLog.isLoading = false;
      state.applicationLog.logs =
        response && response?.length > 0 ? response : [];
      state.applicationLog.error = false;
    });

    builder.addCase(getApplicantHistoryByIdApi.rejected, (state, action) => {
      state.applicationLog.isLoading = false;
      state.applicationLog.logs = [];
      state.applicationLog.error = true;
    });
  },
});

export const {
  jobOpenedbeforeLoginHandler,
  jobSearchFieldsReducer,
  advanceJobSearchReducer,
  addColumnReducer,
  editCloumnhandler,
  removeColumnReducer,
  exitColumnReducer,
  toggleApplicationToProfile,
  sortLogsReducer,
} = jobSlice.actions;

export default jobSlice.reducer;
